import { useEffect, useRef, useState } from 'react';
import { Ellipsis, Pencil, Trash2 } from 'lucide-react';
import { ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CharCounter, countFrom } from '@/components/CharCounter';
import { cn } from '@/lib/utils';

/**
 * CHANGING WHAT YOU ALREADY SAID (docs/5 § D260805-0418) — the menu and the field,
 * for both sizes of the one object.
 *
 * ONE FILE FOR BOTH, AND THEY ARE ALWAYS USED TOGETHER: a surface that offers *Edit*
 * has to have somewhere to type, so the two halves ship as a pair rather than as a
 * folder of parts that could drift. `PostCard` draws them on an open letter or a
 * comment and `Correspondence` on a direct letter, which is what makes editing look
 * like one act in a product where a letter has two sizes.
 *
 * IT IS NOT THE COMPOSER, AND THAT IS DELIBERATE. `/compose` owns a destination, a
 * room, attachments, a persisted draft and a ration; a rewrite has none of those —
 * the letter already knows where it lives, and the only thing that can change is the
 * words. Routing to the composer with a body prefilled would mean a draft that fights
 * the stored one, a Send button that means Save, and a screen whose Cancel is
 * ambiguous. Editing happens on the sheet, where the words are.
 *
 * NO DRAFT PERSISTENCE HERE, WHICH IS THE ONE THING THE COMPOSER HAS THAT THIS DOES
 * NOT. docs/8 § G treats a lost *unsent* letter as a bug, and the failure it names
 * cannot happen here: the words already exist on the server, so a killed WebView
 * loses an amendment rather than a letter. Persisting a half-finished rewrite would
 * mean a sheet that quietly shows something nobody else can see.
 */

/**
 * The overflow menu on your OWN writing: rewrite it, or take it down.
 *
 * TWO ITEMS AND NO THIRD. There is no *Edit history* here even though every previous
 * version is kept — reading them is its own surface with its own copy, and the mark
 * on the sheet is what a reader is owed today (docs/5 § D260805-0418).
 *
 * IT REPLACES THE MENU RATHER THAN JOINING IT. The other menu on a letter is about
 * its author — follow, mute, block, report — and none of those is a thing you do to
 * yourself. So a letter of your own carries this menu and not that one, which also
 * keeps *Delete* out of the same list as *Report*.
 *
 * THE CONFIRMATION IS A SHEET AND IT IS THE ONLY ONE IN THE FLOW. Deleting cannot be
 * undone; editing can, because the words are still in the field until you save. So
 * the destructive act asks and the other does not — a confirm on both would train
 * people to dismiss both.
 */
export function ReviseMenu({
  noun,
  onEdit,
  onDelete,
  className,
}: {
  /** What the thing is called on screen. There is no *post* in this product. */
  noun: 'letter' | 'comment';
  onEdit: () => void;
  /** Resolves when it is gone. A refusal is drawn in the sheet, which stays open. */
  onDelete: () => Promise<unknown>;
  className?: string;
}) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const confirm = async () => {
    setDeleting(true);
    setFailure(null);
    try {
      await onDelete();
      setConfirming(false);
    } catch (err) {
      setFailure(err instanceof ApiError ? err.message : 'That didn’t work.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            className={cn(
              'shrink-0 rounded-full text-paper-muted hover:bg-paper-ink/[0.06] hover:text-paper-ink dark:hover:bg-paper-ink/10',
              className,
            )}
            aria-label={`Change this ${noun}`}
          >
            <Ellipsis aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuItem onSelect={onEdit}>
            <Pencil aria-hidden />
            Edit
          </DropdownMenuItem>
          {/*
            `onSelect` OPENS A SHEET HELD BY THIS COMPONENT rather than wrapping a
            `DialogTrigger`: Radix unmounts the menu on select, and a dialog living
            inside the item would be torn down in the same frame it opened.
          */}
          <DropdownMenuItem
            variant="destructive"
            onSelect={() => {
              setFailure(null);
              setConfirming(true);
            }}
          >
            <Trash2 aria-hidden />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={confirming} onOpenChange={setConfirming}>
        {/* The app's own sheet, up from the bottom edge, padded by its caller —
            see `EditProfileScreen`, which argues both. `aria-describedby` is
            cleared because there is no description: the question IS the copy, and
            a sentence under it explaining that deleting is permanent would be the
            product narrating itself. */}
        <DialogContent
          aria-describedby={undefined}
          className="gap-0 px-5 pt-4 pb-[max(1.5rem,env(safe-area-inset-bottom))]"
        >
          <DialogTitle>Delete this {noun}?</DialogTitle>
          {failure && <p className="mt-3 text-[13px] text-destructive">{failure}</p>}
          {/* Cancel first in the reading direction, which puts the destructive
              button under the thumb LAST rather than where a dismiss usually
              sits. */}
          <div className="mt-5 flex gap-2">
            <Button
              variant="outline"
              className="h-11 flex-1 rounded-full"
              onClick={() => setConfirming(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="h-11 flex-1 rounded-full"
              disabled={deleting}
              onClick={() => void confirm()}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * The words, in a field, in the place they were being read a moment ago.
 *
 * IT SITS WHERE THE BODY SAT, on the sheet, in the sheet's own ink — so editing is
 * plainly a change to this letter rather than a form about it. The typeface is the
 * caller's: a letter is set in `font-letter` and its editor has to be too, or the
 * measure and the line count jump the moment you tap Edit.
 *
 * SAVE IS OFF UNTIL SOMETHING CHANGED. The server treats saving the same words as a
 * no-op and marks nothing, so this is agreement rather than a second rule — but a
 * live button that does nothing is still a button that lies.
 */
export function ReviseField({
  body,
  max,
  onSave,
  onCancel,
  className,
}: {
  body: string;
  /** The ceiling for this size of letter: `POST_MAX_CHARS` or `LETTER_MAX_CHARS`. */
  max: number;
  /** Resolves when the server has it. A refusal is drawn under the field, with the
   *  words still in it — the composer's rule, for the same reason. */
  onSave: (body: string) => Promise<unknown>;
  onCancel: () => void;
  className?: string;
}) {
  const [draft, setDraft] = useState(body);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  /* Grows with the words, as both composers' fields do. */
  const field = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = field.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);

  const trimmed = draft.trim();
  const remaining = max - draft.length;
  const over = remaining < 0;
  const canSave = Boolean(trimmed) && trimmed !== body && !over && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setFailure(null);
    try {
      await onSave(trimmed);
    } catch (err) {
      setFailure(err instanceof ApiError ? err.message : 'That didn’t save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={className}>
      <textarea
        ref={field}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={1}
        autoFocus
        /* `dir="auto"`, as the rendered body and both composers are: the direction
           belongs to the writing, not to the reader's locale. */
        dir="auto"
        aria-label="Edit what you wrote"
        /*
         * A WASH IN THE SHEET'S OWN INK, AND THE NEGATIVE MARGIN IS THE POINT. With
         * a fully transparent field the only thing that changed on tapping *Edit*
         * was two buttons appearing below the words — on a phone the keyboard and
         * the caret say the rest, but on a tablet or with a hardware keyboard
         * nothing did, and a field that looks like a paragraph is a field people
         * retype instead of amend.
         *
         * `-mx-2 px-2` cancels its own inset, so the first character stays on the
         * same vertical line it was being read on: the wash appears AROUND the
         * words rather than moving them. Mixed from `paper-ink` and not from
         * `muted`, which is a cold grey plate off the app's neutral scale and reads
         * as a hole punched in the page (the same reason `Action`'s hover state is
         * mixed this way).
         *
         * THE WIDTH IS `calc(100% + 1rem)` AND NOT `w-full`, WHICH IS THE ONE PART
         * THAT HAD TO BE MEASURED RATHER THAN REASONED. `w-full` pins the box to
         * the container's width, so the negative margin only *shifts* it — it does
         * not widen it — and the field ends up 16px narrower than the paragraph it
         * replaced. The words then rewrap the moment you tap Edit, which is exactly
         * the reflow the padding was chosen to avoid: measured at 430px, the third
         * line lost "think". Growing by the two insets makes the measure identical.
         */
        className="-mx-2 w-[calc(100%+1rem)] resize-none rounded-lg bg-paper-ink/[0.04] px-2 py-1 outline-none dark:bg-paper-ink/[0.07]"
      />

      {failure && <p className="mt-1 text-[13px] text-destructive">{failure}</p>}

      {/* The counter appears late, at the ratio every field in the product shares
          (`countFrom`) — a counter watching every keystroke is a different
          instruction from a limit you approach. */}
      <div className="mt-1.5 flex items-center justify-end gap-2 font-sans">
        <CharCounter
          remaining={remaining}
          show={draft.length >= countFrom(max)}
          over={over}
        />
        <Button
          variant="ghost"
          size="sm"
          className="rounded-full text-paper-muted hover:bg-paper-ink/[0.06] hover:text-paper-ink dark:hover:bg-paper-ink/10"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          className="rounded-full px-4"
          disabled={!canSave}
          onClick={() => void save()}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
